/**
 * Where an AUTHENTICATED ChatKit request is allowed to go.
 *
 * `chatkitFetch` attaches the student's bearer token, `pymble-impersonate` and
 * `X-User-Context` to every request it makes. Those headers are the session, so
 * the destination cannot be model-controlled: the widget's `download_file`
 * client tool and its `download_file` widget action both take a `file_url`
 * straight from the model, and that value reached the wrapper unchecked. An
 * absolute URL pointing anywhere else turned "download this file for me" into
 * "send this student's access token and identity to my host".
 *
 * The rule is the narrow one: authenticated requests go to the API base origin
 * and nowhere else. The origin is DERIVED from the same config the wrapper
 * already uses, never hardcoded, so it follows dev, staging and production
 * without a list to maintain. Nothing else needs allowing - the ChatKit CDN
 * bundle is loaded by a `<script>` tag, not through this fetch, and the
 * widget's own API and upload URLs are both built from that same base.
 *
 * Refusal is the answer, not a headerless retry. A model-directed fetch to an
 * arbitrary host is not a feature we are declining to authenticate; it is not a
 * thing this component does.
 */

/** Thrown when a ChatKit request names a destination we will not authenticate. */
export class ChatKitRequestRefused extends Error {
  constructor(target: string) {
    super(`Refused an authenticated ChatKit request to ${target}`);
    this.name = "ChatKitRequestRefused";
  }
}

/**
 * Validate a ChatKit request target and return what should actually be fetched.
 *
 * Returns the RESOLVED absolute URL rather than the caller's input, because
 * `fetch` resolves a relative path against the DOCUMENT's origin while this
 * guard resolves it against the API base - and in staging and production those
 * are different hosts. Returning the resolved target keeps the URL that was
 * checked and the URL that is fetched the same one.
 *
 * A `Request` already carries an absolute URL and its own method and body, so
 * it is checked and handed back untouched; rebuilding it from a string would
 * drop everything but the URL.
 */
export function resolveChatKitRequest(
  url: string | URL | Request,
  apiBaseUrl: string
): string | URL | Request {
  let base: URL;
  try {
    base = new URL(apiBaseUrl);
  } catch {
    // A misconfigured base must fail closed rather than allow everything.
    throw new ChatKitRequestRefused(String(url));
  }

  if (typeof Request !== "undefined" && url instanceof Request) {
    let requested: URL;
    try {
      requested = new URL(url.url);
    } catch {
      throw new ChatKitRequestRefused(url.url);
    }
    if (requested.origin !== base.origin) {
      throw new ChatKitRequestRefused(requested.href);
    }
    return url;
  }

  const raw = url instanceof URL ? url.href : String(url);
  let resolved: URL;
  try {
    resolved = new URL(raw, base);
  } catch {
    throw new ChatKitRequestRefused(raw);
  }

  // Origin covers scheme, host AND port, so an http:// twin of the API host is
  // refused too - a downgrade would put the token on the wire in clear. A
  // protocol-relative `//host/path` resolves to a foreign origin here and is
  // caught by the same comparison.
  if (resolved.origin !== base.origin) {
    throw new ChatKitRequestRefused(resolved.href);
  }

  return resolved.href;
}
